import { fetchMsgPack } from './utils';

// The tiered device endpoints. Each draws a different amount of the fleet:
// markers for every dot on the map, a page of rows for the visible list, and
// the existing per-id endpoints for the one device with an open status card.

export const PAGE_SIZE = 50;

// The toolbar filters in the server's terms: the parameter names of the
// page, marker and count endpoints, and the field names of the socket
// subscription's filter. Values within one parameter OR together and
// different parameters AND together. With nothing set the server returns
// active devices only; terminated ones come only with the terminated flag,
// and then, as the old list did, nothing but the keyword narrows them.
export const buildFilterParams = ({
  keyword, filter, showTerminated, distanceUnit,
}) => {
  const params = {};
  if (keyword) {
    params.keyword = keyword;
  }
  if (showTerminated) {
    params.terminated = true;
    return params;
  }
  const status = (filter.statuses || []).filter((value) => value === 'online' || value === 'offline');
  if (status.length) {
    params.status = status;
  }
  if (filter.groups?.length) {
    params.groupId = filter.groups;
  }
  if (filter.conditions?.length) {
    params.condition = filter.conditions;
  }
  if (filter.installation?.length) {
    params.installation = filter.installation;
  }
  if (filter.last30DaysTravel?.length) {
    params.travel = filter.last30DaysTravel;
    if (distanceUnit) {
      params.distanceUnit = distanceUnit;
    }
  }
  return params;
};

export const buildFilterQuery = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([name, value]) => {
    (Array.isArray(value) ? value : [value]).forEach((item) => search.append(name, item));
  });
  return search.toString();
};

export const fetchMarkers = (query = '') => fetchMsgPack(`/api/devices/markers${query ? `?${query}` : ''}`);

// The first page is asked for without a cursor. Every page answers with
// `next`, an opaque cursor for its last row, or null on the final page; the
// page after it is asked for with that cursor. A cursor is bound to the sort,
// order and filter it was issued under. A page with no cursor to reach it
// from, such as the one a selected row sits on, is asked for by offset.
export const fetchDevicePage = ({
  cursor = null, offset = 0, limit = PAGE_SIZE, sort = 'name', order = 'asc', query = '',
}) => {
  const params = new URLSearchParams({ limit, sort, order });
  if (cursor) {
    params.set('cursor', cursor);
  } else if (offset > 0) {
    params.set('offset', offset);
  }
  const suffix = query ? `&${query}` : '';
  return fetchMsgPack(`/api/devices/page?${params.toString()}${suffix}`);
};

// Where one device sits in the list under a sort and filter, as
// { offset } counted from 0, or { offset: null } when the filter excludes
// it. Lets the list scroll to a device selected from the map before its
// page has loaded.
export const fetchDeviceRank = ({
  id, sort = 'name', order = 'asc', query = '',
}) => {
  const params = new URLSearchParams({ id, sort, order });
  const suffix = query ? `&${query}` : '';
  return fetchMsgPack(`/api/devices/rank?${params.toString()}${suffix}`);
};

// Fleet-wide bucket sizes for the filter menu, computed with the same
// predicates the filters use.
export const fetchDeviceCounts = (distanceUnit) => {
  const params = new URLSearchParams();
  if (distanceUnit) {
    params.set('distanceUnit', distanceUnit);
  }
  const query = params.toString();
  return fetchMsgPack(`/api/devices/counts${query ? `?${query}` : ''}`);
};

const idQuery = (name, ids) => ids.map((id) => `${name}=${encodeURIComponent(id)}`).join('&');

export const fetchDevicesById = (ids) => (ids.length
  ? fetchMsgPack(`/api/devices?${idQuery('id', ids)}`)
  : Promise.resolve([]));

export const fetchPositionsById = (ids) => (ids.length
  ? fetchMsgPack(`/api/positions?${idQuery('id', ids)}`)
  : Promise.resolve([]));

export const fetchLatestPosition = (deviceId) => fetchMsgPack(`/api/positions?deviceId=${deviceId}`);

// The server ignores a malformed subscription without saying so, so only
// integer ids ever go out, and each once.
export const cleanIds = (ids) => Array.from(new Set((ids || []).filter(Number.isInteger)));
